import { useState } from 'react';
import type { GhostHighlight } from 'react-pdf-highlighter-extended';
import {
  DocumentFieldAPIModel,
  endpointDocumentFieldPOST,
} from '~/api/def/endpoints/document/documentFieldEndpoint';
import { FieldIdObj } from '~/components/ui/vendor/pdf/document_pdf_viewer/FieldCorrectionForm';
import type { BoundingBox } from './types';

export type SetDocumentFieldArgs = {
  highlight: GhostHighlight;
  fieldId: string;
  documentId: string;
  fieldValue: string;
};

/**
 * The fieldManager handles submission of field updates
 * and tracks current submission state.
 * In particular for errors we wish to be explicit with tracking
 * pieces individually per field, most of the actual state
 * lives inside the field-specific fetcher.
 */
export function useFieldManager(arg: {
  fieldId: string | null;
  documentId: string;
}) {
  const [updating, setUpdating] = useState(false);
  const [error, setError] = useState(false);

  async function handleSetDocumentField(arg: {
    highlight: BoundingBox;
    fieldId: FieldIdObj;
    documentId: string;
    fieldValue: string;
  }) {
    if (!arg.fieldId) {
      throw new Error('Unable to set field without a selected field');
    }
    setUpdating(true);
    const result = await setDocumentField({
      highlight: arg.highlight,
      fieldId: arg.fieldId,
      documentId: arg.documentId,
      fieldValue: arg.fieldValue,
    });
    setUpdating(false);
    setError(!!result?.success);
    return result?.data;
  }

  /**
   *  copy of existing field logic
   *  TODO: replace with delete call once implemented in api
   * @param arg
   */

  async function handleRemoveDocumentField() {
    if (!arg.fieldId) {
      throw new Error('Unable to set field without a selected field');
    }
    setUpdating(true);
    const result = await removeDocumentField(arg.documentId, arg.fieldId);
    setUpdating(false);
    setError(!!result?.success);
    return result?.data;
  }
  return {
    handleSetDocumentField,
    handleRemoveDocumentField,
    updating,
    error,
  } as const;
}

export async function removeDocumentField(documentId: string, fieldId: string) {
  if (!fieldId) {
    throw new Error('Unable to set field without a selected field');
  }
  const req: DocumentFieldAPIModel = {
    id: fieldId,
    value: '',
    name: '',
    startXCoordinate: 0,
    endXCoordinate: 0,
    startYCoordinate: 0,
    endYCoordinate: 0,
    startPageNo: 0,
    endPageNo: 0,
    // isPresent: false,
    // boundingBox: null,
  };
  return await endpointDocumentFieldPOST(documentId, fieldId, req);
}

export async function setDocumentField(arg: {
  highlight: BoundingBox;
  fieldId: FieldIdObj;
  documentId: string;
  fieldValue: string;
}) {
  if (!arg.fieldId) {
    throw new Error('Unable to set field without a selected field');
  }
  const bbox: BoundingBox = arg.highlight;
  const req: DocumentFieldAPIModel = {
    id: arg.fieldId.id,
    value: arg.fieldValue,
    name: arg.fieldId.name,
    startXCoordinate: bbox.x1,
    endXCoordinate: bbox.x2,
    startYCoordinate: bbox.y1,
    endYCoordinate: bbox.y2,
    startPageNo: bbox.pageNumber,
    endPageNo: bbox.pageNumber,
  };
  return await endpointDocumentFieldPOST(arg.documentId, arg.fieldId.id, req);
}
